import { FoodsIconButton, OptionsPopover, type OptionsPopoverOption, ScoreOverviewCard } from "@foodpilot/foods";
import { Box, Popover, Stack, useTheme } from "@mui/material";
import { useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import type { OlapScoresWithPrevScores } from "src/api/client-api/olapScores/olapScoresSchema";
import { useLanguage } from "src/context/LanguageProvider";
import { OlapScoresService } from "src/context/OlapScores/OlapScoresService";
import type { ScoreWithVariation } from "src/services/ScoreService";
import type { Score } from "src/types";
import { formatNumber } from "src/utils/formatting";

type ProductScoreOverviewCardsProps = {
    areScoresRefetching: boolean;
    entityScores: Score[];
    lastSelectedVariation: ScoreWithVariation | undefined;
    mainScores: ScoreWithVariation[];
    onScoreSelect: (scoreId: number, variationId: number | null) => void;
    onVariationSelect: (scoreId: number, variationId: number | null) => void;
    scores: OlapScoresWithPrevScores | undefined;
    scoresWithVariations: ScoreWithVariation[];
    selectedScoreId: number;
    selectedVariationIds: Record<number, number | null> | undefined;
};

type ScorePresentation = {
    icon: "carbonEmission" | "drop" | "environment" | "scoresIndicators";
};

const resolveScorePresentation = (score: ScoreWithVariation, scoreDefinition: Score | undefined): ScorePresentation => {
    const identity = [scoreDefinition?.code, scoreDefinition?.type, score.type, score.group]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

    if (identity.includes("water")) {
        return { icon: "drop" };
    }

    if (identity.includes("climate") || identity.includes("carbon") || identity.includes("co2")) {
        return { icon: "carbonEmission" };
    }

    if (identity.includes("pef")) {
        return { icon: "scoresIndicators" };
    }

    return { icon: "environment" };
};

const resolveSelectedVariation = (
    score: ScoreWithVariation,
    scoreVariations: ScoreWithVariation[],
    selectedVariationIds: Record<number, number | null> | undefined,
) => {
    const selectedVariationId = selectedVariationIds?.[score.scoreId];

    return (
        scoreVariations.find(({ variationId }) => variationId === selectedVariationId) ??
        scoreVariations.find(({ defaultDisplay }) => defaultDisplay) ??
        score
    );
};

export const ProductScoreOverviewCards = (props: ProductScoreOverviewCardsProps) => {
    const {
        areScoresRefetching,
        entityScores,
        lastSelectedVariation,
        mainScores,
        onScoreSelect,
        onVariationSelect,
        scores,
        scoresWithVariations,
        selectedScoreId,
        selectedVariationIds,
    } = props;
    const { t } = useTranslation();
    const { currentLanguage } = useLanguage();
    const theme = useTheme();
    const cardsRowRef = useRef<HTMLDivElement>(null);
    const [variationMenu, setVariationMenu] = useState<{ anchor: HTMLElement; scoreId: number } | null>(null);

    const closeVariationMenu = () => setVariationMenu(null);

    return (
        <Box sx={{ maxWidth: "100%", position: "relative" }}>
            <Stack
                aria-label={t("product.v2.scores.overview.list", "Scores and key indicators")}
                direction="row"
                alignItems="flex-start"
                gap={theme.spacing(3)}
                ref={cardsRowRef}
                role="group"
                sx={{
                    overflowX: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                }}
            >
                {mainScores.map((mainScore) => {
                    const isSelected = selectedScoreId === mainScore.scoreId;
                    const scoreVariations = scoresWithVariations.filter(({ scoreId }) => scoreId === mainScore.scoreId);
                    const selectedVariation = resolveSelectedVariation(
                        mainScore,
                        scoreVariations,
                        selectedVariationIds,
                    );
                    const scoreDefinition = entityScores.find(({ id }) => id === mainScore.scoreId);
                    const presentation = resolveScorePresentation(mainScore, scoreDefinition);
                    const menuId = `product-score-${mainScore.scoreId}-variations`;
                    const menuOpen = variationMenu?.scoreId === mainScore.scoreId;
                    const scoreValue = OlapScoresService.normalizeNumericalScore(
                        scores?.olapScores[mainScore.scoreId]?.score.value,
                    );
                    const formattedValue =
                        scoreValue === null ? null : (
                            formatNumber(scoreValue, currentLanguage, {
                                maximumFractionDigits: selectedVariation.precision,
                            })
                        );
                    const commonProps = {
                        icon: presentation.icon,
                        onSelect: () => onScoreSelect(mainScore.scoreId, selectedVariation.variationId),
                        selected: isSelected,
                        selectionLabel: t("product.v2.scores.overview.select", "Show {{score}} details", {
                            score: mainScore.group,
                        }),
                        title: mainScore.group,
                        unit: selectedVariation.unit,
                        variationAction: {
                            ariaLabel: t("product.v2.scores.overview.change_variation", "Change {{score}} variation", {
                                score: mainScore.group,
                            }),
                            controls: menuId,
                            onOpen: (event: MouseEvent<HTMLElement>) => {
                                event.stopPropagation();
                                setVariationMenu({ anchor: event.currentTarget, scoreId: mainScore.scoreId });
                            },
                            open: menuOpen,
                        },
                        variationLabel: selectedVariation.label,
                    };
                    const isLoading =
                        scores === undefined ||
                        (areScoresRefetching && lastSelectedVariation?.scoreId === mainScore.scoreId);

                    return (
                        <Stack
                            key={mainScore.scoreId}
                            onClickCapture={(event) => {
                                const variationButton =
                                    event.currentTarget.querySelector<HTMLButtonElement>(
                                        'button[aria-haspopup="menu"]',
                                    );
                                const variationTrigger = variationButton?.parentElement;

                                if (
                                    variationTrigger === null ||
                                    variationTrigger === undefined ||
                                    !variationTrigger.contains(event.target as Node)
                                ) {
                                    return;
                                }

                                event.preventDefault();
                                event.stopPropagation();
                                setVariationMenu({ anchor: variationTrigger, scoreId: mainScore.scoreId });
                            }}
                            sx={{
                                boxSizing: "border-box",
                                flexShrink: 0,
                                height: theme.custom.scoreOverviewCard.heights.selected,
                                paddingBottom: isSelected ? theme.spacing(0) : theme.spacing(2),
                                width: theme.custom.scoreOverviewCard.width,
                                "& > :first-of-type": {
                                    ...(isSelected && {
                                        borderBottomLeftRadius: theme.spacing(0),
                                        borderBottomRightRadius: theme.spacing(0),
                                    }),
                                    height: "100%",
                                    width: "100%",
                                },
                                "& > :first-of-type div:has(> button[aria-haspopup='menu'])": {
                                    alignSelf: "flex-start",
                                    borderRadius: theme.spacing(2),
                                    cursor: "pointer",
                                    gap: theme.spacing(1),
                                    marginInlineStart: theme.spacing(-1),
                                    paddingInline: theme.spacing(1),
                                    pointerEvents: "auto",
                                    position: "relative",
                                    width: "fit-content",
                                    zIndex: 3,
                                    "&:hover": {
                                        backgroundColor: theme.custom.grey[600],
                                    },
                                    "&:active": {
                                        backgroundColor: theme.custom.grey[1000],
                                    },
                                    "& > button": {
                                        backgroundColor: "transparent",
                                        "&:hover, &:active": {
                                            backgroundColor: "transparent",
                                        },
                                    },
                                },
                            }}
                        >
                            {isLoading ?
                                <ScoreOverviewCard {...commonProps} isLoading />
                            : formattedValue === null ?
                                <ScoreOverviewCard
                                    {...commonProps}
                                    emptyLabel={t("product.v2.scores.overview.empty", "{{score}} score unavailable", {
                                        score: mainScore.group,
                                    })}
                                    value={null}
                                />
                            :   <ScoreOverviewCard {...commonProps} value={formattedValue} />}
                            <Popover
                                anchorEl={menuOpen ? variationMenu.anchor : null}
                                anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                                elevation={0}
                                id={menuId}
                                onClose={closeVariationMenu}
                                open={menuOpen}
                                slotProps={{
                                    paper: {
                                        sx: {
                                            borderRadius: theme.spacing(1.5),
                                            boxShadow: theme.custom.shadows.popover,
                                        },
                                    },
                                }}
                                transformOrigin={{ horizontal: "left", vertical: "top" }}
                            >
                                <OptionsPopover
                                    content={[
                                        {
                                            header: mainScore.group,
                                            options: scoreVariations.map((variation) => ({
                                                disabled: variation.disabled,
                                                id: variation.rowId,
                                                label: variation.label,
                                            })),
                                        },
                                    ]}
                                    defaultIndex={selectedVariation.rowId}
                                    minWidth={theme.custom.scoreOverviewCard.width}
                                    onChange={(option: OptionsPopoverOption) => {
                                        const variation = scoreVariations.find(({ rowId }) => rowId === option.id);

                                        if (variation === undefined) {
                                            return;
                                        }

                                        onVariationSelect(variation.scoreId, variation.variationId);
                                        closeVariationMenu();
                                    }}
                                />
                            </Popover>
                        </Stack>
                    );
                })}
            </Stack>
            {mainScores.length > 3 && (
                <Stack
                    data-testid="product-score-overview-carousel-control"
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        height: theme.custom.scoreOverviewCard.heights.inactive,
                        pointerEvents: "none",
                        position: "absolute",
                        right: {
                            xs: 0,
                            md: theme.custom.scoreOverviewCard.carouselControl.inlineEndOffset,
                        },
                        top: 0,
                    }}
                >
                    <FoodsIconButton
                        icon="arrowRightShort"
                        size="m"
                        buttonProps={{
                            "aria-label": t("product.v2.scores.overview.scroll_next", "Show more scores"),
                            onClick: () => {
                                const cardsRow = cardsRowRef.current;
                                cardsRow?.scrollBy({ behavior: "smooth", left: cardsRow.clientWidth });
                            },
                            sx: { pointerEvents: "auto" },
                        }}
                    />
                </Stack>
            )}
        </Box>
    );
};
